import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, 
  Send, 
  Crown, 
  User,
  MessageSquare,
  Clock,
  Lock
} from "lucide-react";
import { toast } from "sonner";

export default function MessagesPage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (userId && isPremium) {
      fetchConversation(userId);
    }
  }, [userId, isPremium]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      setConversations(response.data);
      setIsPremium(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsPremium(false);
      } else {
        toast.error("Failed to load messages");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (recipientId) => {
    try {
      const response = await axios.get(`${API}/messages/${recipientId}`);
      setActiveConversation(response.data.user);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error("Failed to load conversation");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        recipient_id: activeConversation.user_id,
        content: newMessage
      });
      
      setMessages([...messages, response.data]);
      setNewMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading messages...</div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <Crown className="h-12 w-12 text-gold mx-auto mb-4" />
            <h2 className="font-serif text-xl font-bold mb-2">Premium Feature</h2>
            <p className="text-muted-foreground mb-4">
              Direct messaging is available to Premium subscribers. Upgrade now to message other law students directly!
            </p>
            <Button onClick={() => navigate("/subscribe")} className="bg-gold text-primary-foreground">
              Upgrade to Premium
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="messages-page">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/community")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Community
          </Button>
          
          <div className="flex items-center gap-3">
            <Crown className="h-8 w-8 text-gold" />
            <div>
              <h1 className="font-serif text-2xl font-bold">Direct Messages</h1>
              <p className="text-sm text-muted-foreground">Premium feature</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversations List */}
          <Card className="lg:col-span-1 h-[600px] flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Conversations</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-0">
              {conversations.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No conversations yet</p>
                  <p className="text-xs mt-1">Start chatting from user profiles</p>
                </div>
              ) : (
                <div className="divide-y">
                  {conversations.map(conv => (
                    <button
                      key={conv.user.user_id}
                      className={`w-full p-4 text-left hover:bg-accent transition-colors ${
                        activeConversation?.user_id === conv.user.user_id ? 'bg-accent' : ''
                      }`}
                      onClick={() => {
                        navigate(`/messages/${conv.user.user_id}`);
                        fetchConversation(conv.user.user_id);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                          {conv.user.is_premium ? (
                            <Crown className="h-5 w-5 text-gold" />
                          ) : (
                            <User className="h-5 w-5 text-gold" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">{conv.user.name}</span>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-gold text-primary-foreground">
                                {conv.unread_count}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {conv.last_message.is_sender ? "You: " : ""}{conv.last_message.content}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Message Thread */}
          <Card className="lg:col-span-2 h-[600px] flex flex-col">
            {activeConversation ? (
              <>
                <CardHeader className="pb-3 border-b">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                      {activeConversation.is_premium ? (
                        <Crown className="h-5 w-5 text-gold" />
                      ) : (
                        <User className="h-5 w-5 text-gold" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{activeConversation.name}</CardTitle>
                      {activeConversation.badges?.map(badge => (
                        <Badge key={badge.id} variant="outline" className="text-xs mr-1" style={{ color: badge.color }}>
                          {badge.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map(msg => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] rounded-lg p-3 ${
                        msg.sender_id === user?.user_id 
                          ? 'bg-gold text-primary-foreground' 
                          : 'bg-card border'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.sender_id === user?.user_id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        }`}>
                          {formatTimeAgo(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </CardContent>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      data-testid="message-input"
                    />
                    <Button 
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="bg-gold text-primary-foreground"
                      data-testid="send-message-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
